'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { LayoutGrid, Trash2, Plus, Search, Image as ImageIcon, FileText, ChevronDown, Users, Hash, X, User, Tag, Presentation, BarChart3, GripVertical, Wand2, Mail, Banknote, Coins, Flag, Settings2, HeartPulse, Landmark, AppWindow, Megaphone, Cpu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { Avatar } from '@/components/avatar';
import { DeckTab } from '@/components/admin/deck-tab';
import { AnalyticsTab } from '@/components/admin/analytics-tab';
import { HealthTab } from '@/components/admin/health-tab';
import { ModerationTab } from '@/components/admin/moderation-tab';
import { EmailTab } from '@/components/admin/email-tab';
import { EarningsTab } from '@/components/admin/earnings-tab';
import { AttributionTab } from '@/components/admin/attribution-tab';
import { EconomicsTab } from '@/components/admin/economics-tab';
import { SettingsTab } from '@/components/admin/settings-tab';
import { AdsTab } from '@/components/admin/ads-tab';
import { ModelsTab } from '@/components/admin/models-tab';
import { deriveEntries, moveEntry, renumber, type CurationEntry } from '@/lib/admin/curation-entries';

interface FeaturedItem {
  id: string;
  contentType: string;
  postId: string | null;
  targetUserId: string | null;
  targetChannelId: string | null;
  sortOrder: number;
  displayMode: string;
  showBadge: boolean;
  createdAt: string;
  post: {
    id: string;
    title: string | null;
    content: string;
    mediaType: string | null;
    mediaUrl: string | null;
    thumbnailUrl: string | null;
    createdAt: string;
  } | null;
  author: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    accountType: string;
  } | null;
  channel: {
    id: string;
    name: string;
    slug: string;
    iconEmoji?: string;
    logoUrl?: string | null;
  } | null;
  targetUser?: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    accountType: string;
  } | null;
  targetChannel?: {
    id: string;
    name: string;
    slug: string;
    iconEmoji?: string;
    logoUrl?: string | null;
    memberCount?: number;
    postCount?: number;
    /** Apps ladder: false = hidden from the catalog, still reachable by link. */
    listed?: boolean;
  } | null;
  targetTag?: {
    id: string;
    name: string;
    useCount?: number;
    color?: string | null;
  } | null;
}

// A section's rows: curated items interleaved with spacers. A spacer is a run
// of slots left to the ranking algorithm; it exists only as a gap in the
// items' sortOrder numbering, never as a stored row. The arithmetic lives in
// lib/admin/curation-entries.ts, where it is unit-tested.
type SectionEntry = CurationEntry<FeaturedItem>;

interface SearchResult {
  id: string;
  title?: string | null;
  content?: string;
  mediaType?: string | null;
  thumbnailUrl?: string | null;
  username?: string;
  displayName?: string;
  avatarUrl?: string | null;
  accountType?: string;
  name?: string;
  slug?: string;
  iconEmoji?: string;
  logoUrl?: string | null;
  memberCount?: number;
  postCount?: number;
  bio?: string | null;
  useCount?: number;
  color?: string | null;
  listed?: boolean;
  author?: { id: string; username: string; displayName: string; avatarUrl: string | null; accountType: string } | null;
  channel?: { id: string; name: string; slug: string } | null;
}

function SearchPicker({ searchType, onSelect, onCancel, existingIds }: {
  searchType: 'posts' | 'agents' | 'channels' | 'tags' | 'users' | 'apps';
  onSelect: (item: SearchResult) => void;
  onCancel: () => void;
  existingIds: Set<string>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>(null);
  const t = useT();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/admin/search?type=${searchType}&q=${encodeURIComponent(query.trim())}`);
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || []);
        }
      } catch {
        // ignore
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, searchType]);

  const placeholder = searchType === 'posts'
    ? t('admin.searchPostsPlaceholder')
    : searchType === 'agents'
    ? t('admin.searchAgentsPlaceholder')
    : searchType === 'tags'
    ? t('admin.searchTagsPlaceholder')
    : searchType === 'users'
    ? t('admin.searchUsersPlaceholder')
    : searchType === 'apps'
    ? t('admin.searchAppsPlaceholder')
    : t('admin.searchChannelsPlaceholder');

  return (
    <div className="px-3 py-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
          />
        </div>
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {searching && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1 py-1">
          <Spinner size="xs" />
          {t('admin.searching')}
        </div>
      )}

      {results.length > 0 && (
        <div className="max-h-60 overflow-y-auto rounded-lg border border-border divide-y divide-border/50">
          {results.map(item => {
            const alreadyAdded = existingIds.has(item.id);
            return (
              <button
                key={item.id}
                disabled={alreadyAdded}
                onClick={() => onSelect(item)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
                  alreadyAdded ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent cursor-pointer'
                )}
              >
                {searchType === 'posts' && (
                  <>
                    {item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt="" className="w-10 h-10 rounded-md object-cover shrink-0 bg-muted" />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.title || (item.content || '').slice(0, 60) || 'Untitled'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        @{item.author?.username || 'unknown'}
                        {item.channel && ` · //${item.channel.slug}`}
                        {item.mediaType && ` · ${item.mediaType}`}
                      </p>
                    </div>
                    {alreadyAdded && <span className="text-xs text-muted-foreground shrink-0">{t('admin.added')}</span>}
                  </>
                )}

                {searchType === 'agents' && (
                  <>
                    <Avatar seed={item.username || ''} type="agent" src={item.avatarUrl} className="h-8 w-8 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.displayName || item.username}</p>
                      <p className="text-xs text-muted-foreground truncate">@{item.username}</p>
                    </div>
                    {alreadyAdded && <span className="text-xs text-muted-foreground shrink-0">{t('admin.added')}</span>}
                  </>
                )}

                {searchType === 'channels' && (
                  <>
                    <Avatar seed={item.slug || ''} type="channel" src={item.logoUrl} className="h-8 w-8 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">//{item.slug}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('admin.members', { count: item.memberCount ?? 0 })} · {t('admin.postsCount', { count: item.postCount ?? 0 })}
                      </p>
                    </div>
                    {alreadyAdded && <span className="text-xs text-muted-foreground shrink-0">{t('admin.added')}</span>}
                  </>
                )}

                {searchType === 'apps' && (
                  <>
                    <Avatar seed={item.slug || ''} type="channel" src={item.logoUrl} className="h-8 w-8 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        //{item.slug}
                        {item.listed === false && ` · ${t('admin.hidden')}`}
                      </p>
                    </div>
                    {alreadyAdded && <span className="text-xs text-muted-foreground shrink-0">{t('admin.added')}</span>}
                  </>
                )}

                {searchType === 'tags' && (
                  <>
                    <span className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">#{item.name}</p>
                      <p className="text-xs text-muted-foreground">{t('admin.uses', { count: item.useCount ?? 0 })}</p>
                    </div>
                    {alreadyAdded && <span className="text-xs text-muted-foreground shrink-0">{t('admin.added')}</span>}
                  </>
                )}

                {searchType === 'users' && (
                  <>
                    <Avatar seed={item.username || ''} type="human" src={item.avatarUrl} className="h-8 w-8 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.displayName || item.username}</p>
                      <p className="text-xs text-muted-foreground truncate">@{item.username}</p>
                    </div>
                    {alreadyAdded && <span className="text-xs text-muted-foreground shrink-0">{t('admin.added')}</span>}
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p className="text-xs text-muted-foreground px-1 py-1">{t('admin.noResultsFound')}</p>
      )}
    </div>
  );
}

function ContentTab() {
  const [items, setItems] = useState<FeaturedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingType, setAddingType] = useState</* 'carousel_post' | */ 'featured_post' | 'marketplace_post' | 'trending_agent' | 'trending_channel' | 'featured_app' | 'featured_tag' | 'featured_user' | null>(null);
  const [addError, setAddError] = useState('');
  const t = useT();

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/featured');
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // const carouselItems = items.filter(i => i.contentType === 'carousel_post');
  const featuredItems = items.filter(i => i.contentType === 'featured_post');
  const marketplaceItems = items.filter(i => i.contentType === 'marketplace_post');
  const trendingAgents = items.filter(i => i.contentType === 'trending_agent');
  const trendingChannels = items.filter(i => i.contentType === 'trending_channel');
  const curatedApps = items.filter(i => i.contentType === 'featured_app');
  const featuredTags = items.filter(i => i.contentType === 'featured_tag');
  const featuredUsers = items.filter(i => i.contentType === 'featured_user');

  const handleAddPost = async (contentType: 'carousel_post' | 'featured_post' | 'marketplace_post', result: SearchResult) => {
    setAddError('');
    const existing = items.filter(i => i.contentType === contentType);
    const sortOrder = existing.length > 0 ? Math.max(...existing.map(i => i.sortOrder)) + 1 : 0;

    try {
      const res = await fetch('/api/admin/featured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType, postId: result.id, sortOrder }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.error || t('admin.failedToAdd'));
      } else {
        setAddingType(null);
        await fetchItems();
      }
    } catch {
      setAddError(t('admin.networkError'));
    }
  };

  const handleAddAgent = async (result: SearchResult) => {
    setAddError('');
    const sortOrder = trendingAgents.length > 0 ? Math.max(...trendingAgents.map(i => i.sortOrder)) + 1 : 0;
    try {
      const res = await fetch('/api/admin/featured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: 'trending_agent', targetUserId: result.id, sortOrder }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.error || t('admin.failedToAdd'));
      } else {
        setAddingType(null);
        await fetchItems();
      }
    } catch {
      setAddError(t('admin.networkError'));
    }
  };

  const handleAddChannel = async (result: SearchResult) => {
    setAddError('');
    const sortOrder = trendingChannels.length > 0 ? Math.max(...trendingChannels.map(i => i.sortOrder)) + 1 : 0;
    try {
      const res = await fetch('/api/admin/featured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: 'trending_channel', targetChannelId: result.id, sortOrder }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.error || t('admin.failedToAdd'));
      } else {
        setAddingType(null);
        await fetchItems();
      }
    } catch {
      setAddError(t('admin.networkError'));
    }
  };

  // The Apps ladder pins the marketplace LISTING channel, same as a curated
  // channel, under its own content type so ordering the catalog never touches
  // the community channels shelf.
  const handleAddApp = async (result: SearchResult) => {
    setAddError('');
    const sortOrder = curatedApps.length > 0 ? Math.max(...curatedApps.map(i => i.sortOrder)) + 1 : 0;
    try {
      const res = await fetch('/api/admin/featured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: 'featured_app', targetChannelId: result.id, sortOrder }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.error || t('admin.failedToAdd'));
      } else {
        setAddingType(null);
        await fetchItems();
      }
    } catch {
      setAddError(t('admin.networkError'));
    }
  };

  // Catalog visibility. A hidden app keeps its slot in this list so the choice
  // is reversible; Discover leaves that slot to the algorithm and the app stays
  // reachable by direct link.
  const handleToggleListed = async (item: FeaturedItem) => {
    await fetch('/api/admin/featured', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.id, listed: item.targetChannel?.listed === false }),
    });
    await fetchItems();
  };

  const handleAddTag = async (result: SearchResult) => {
    setAddError('');
    const sortOrder = featuredTags.length > 0 ? Math.max(...featuredTags.map(i => i.sortOrder)) + 1 : 0;
    try {
      const res = await fetch('/api/admin/featured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: 'featured_tag', targetTagId: result.id, sortOrder }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.error || t('admin.failedToAdd'));
      } else {
        setAddingType(null);
        await fetchItems();
      }
    } catch {
      setAddError(t('admin.networkError'));
    }
  };

  const handleAddUser = async (result: SearchResult) => {
    setAddError('');
    const sortOrder = featuredUsers.length > 0 ? Math.max(...featuredUsers.map(i => i.sortOrder)) + 1 : 0;
    try {
      const res = await fetch('/api/admin/featured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: 'featured_user', targetUserId: result.id, sortOrder }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.error || t('admin.failedToAdd'));
      } else {
        setAddingType(null);
        await fetchItems();
      }
    } catch {
      setAddError(t('admin.networkError'));
    }
  };

  const handleRemove = async (id: string) => {
    await fetch(`/api/admin/featured?id=${id}`, { method: 'DELETE' });
    await fetchItems();
  };

  // --- drag-to-reorder for the curated lists -------------------------------
  // Grab a row and drop it where you want it; reordering is scoped to a single
  // section (same `contentType`). `sortOrder` is the SLOT INDEX in the final
  // list (consumers do `result[sortOrder] = item` and backfill empty slots
  // algorithmically), so gaps in the numbering are meaningful: they are slots
  // the algorithm fills. Spacers are the UI for those gaps. They are not
  // stored anywhere; a spacer of size N is derived from (and persisted as) a
  // jump of N in the following item's sortOrder.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragCt, setDragCt] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const listForType = (ct: string): FeaturedItem[] =>
    items.filter((i) => i.contentType === ct).sort((a, b) => a.sortOrder - b.sortOrder);

  const entriesForType = (ct: string): SectionEntry[] => deriveEntries(listForType(ct), ct);

  // Renumber sortOrder = slot position (spacers advance the position by their
  // size), PATCH the rows that moved, and stamp the new order locally first so
  // the list doesn't flicker. A trailing spacer still persists as nothing —
  // every slot after the last curated row is algorithmic anyway — which is why
  // `moveEntry` clamps a dragged spacer before the final row rather than
  // letting the drop destroy it.
  const persistEntries = async (entries: SectionEntry[]) => {
    const rank = renumber(entries);
    const changed = [...rank.entries()].filter(
      ([id, r]) => items.find((i) => i.id === id)?.sortOrder !== r,
    );
    if (!changed.length) return;
    setItems((prev) => prev.map((p) => (rank.has(p.id) ? { ...p, sortOrder: rank.get(p.id)! } : p)));
    await Promise.all(
      changed.map(([id, r]) =>
        fetch('/api/admin/featured', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, sortOrder: r }),
        }),
      ),
    );
    await fetchItems();
  };

  const entryDrag = (ct: string, key: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setDragKey(key);
      setDragCt(ct);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', key);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!dragKey || dragKey === key || dragCt !== ct) return; // same section only
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (overKey !== key) setOverKey(key);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const from = dragKey;
      const fromCt = dragCt;
      setDragKey(null);
      setDragCt(null);
      setOverKey(null);
      if (!from || from === key || fromCt !== ct) return;
      void persistEntries(moveEntry(entriesForType(ct), from, key));
    },
    onDragEnd: () => { setDragKey(null); setDragCt(null); setOverKey(null); },
  });

  const dragBind = (item: FeaturedItem) => entryDrag(item.contentType, item.id);

  // Insert a spacer at the top of the section (slot 0 goes to the algorithm);
  // drag it into place from there. Growing an existing leading spacer instead
  // of stacking a second one keeps the derived representation canonical.
  const handleAddSpacer = (ct: string) => {
    const entries = entriesForType(ct);
    if (!entries.length) return;
    const [head, ...rest] = entries;
    void persistEntries(
      head.kind === 'spacer'
        ? [{ ...head, size: head.size + 1 }, ...rest]
        : [{ kind: 'spacer', key: `spacer:${ct}:new`, size: 1 }, ...entries],
    );
  };

  const handleSpacerResize = (ct: string, key: string, size: number) => {
    const entries = entriesForType(ct);
    const next = size <= 0
      ? entries.filter((e) => e.key !== key)
      : entries.map((e) => (e.key === key && e.kind === 'spacer' ? { ...e, size: Math.min(size, 50) } : e));
    void persistEntries(next);
  };

  const handleToggleBadge = async (item: FeaturedItem) => {
    await fetch('/api/admin/featured', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.id, showBadge: !item.showBadge }),
    });
    await fetchItems();
  };

  const handleToggleDisplayMode = async (item: FeaturedItem) => {
    const newMode = item.displayMode === 'always' ? 'once' : 'always';
    await fetch('/api/admin/featured', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.id, displayMode: newMode }),
    });
    await fetchItems();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" tone="muted" />
      </div>
    );
  }

  const renderPostRow = (item: FeaturedItem, _idx: number, _total: number, type: 'carousel_post' | 'featured_post' | 'marketplace_post') => (
    <div
      key={item.id}
      {...dragBind(item)}
      className={cn(
        'group flex items-center gap-2.5 px-4 py-2.5 transition-colors',
        overKey === item.id && dragKey !== item.id && 'bg-primary/5',
        dragKey === item.id && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        title="Drag to reorder"
        className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      {item.post?.thumbnailUrl ? (
        <img
          src={item.post.thumbnailUrl}
          alt=""
          className="w-10 h-10 rounded-md object-cover shrink-0 bg-muted"
        />
      ) : (
        <div className="w-10 h-10 rounded-md shrink-0 bg-muted flex items-center justify-center">
          <FileText className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {item.post?.title || (item.post?.content || '').slice(0, 60)}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          @{item.author?.username || 'unknown'}
          {item.channel && ` ${t('admin.inChannel', { slug: item.channel.slug })}`}
        </p>
      </div>

      {type === 'featured_post' && (
        <button
          onClick={() => handleToggleDisplayMode(item)}
          className={cn(
            'text-xs px-2 py-0.5 rounded-full border transition-colors shrink-0',
            item.displayMode === 'always'
              ? 'border-primary/30 text-primary bg-primary/5'
              : 'border-muted-foreground/30 text-muted-foreground'
          )}
        >
          {item.displayMode === 'always' ? t('admin.always') : t('admin.once')}
        </button>
      )}

      <div className="flex items-center gap-1.5 shrink-0">
        <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <input type="checkbox" checked={item.showBadge} onChange={() => handleToggleBadge(item)} className="accent-primary" />
          {t('admin.pin')}
        </label>
        <button
          onClick={() => handleRemove(item.id)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  const renderAgentRow = (item: FeaturedItem) => (
    <div
      key={item.id}
      {...dragBind(item)}
      className={cn(
        'group flex items-center gap-2.5 px-4 py-2.5 transition-colors',
        overKey === item.id && dragKey !== item.id && 'bg-primary/5',
        dragKey === item.id && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        title="Drag to reorder"
        className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <Avatar seed={item.targetUser?.username || ''} type="agent" src={item.targetUser?.avatarUrl} className="h-8 w-8 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.targetUser?.displayName || item.targetUser?.username}</p>
        <p className="text-xs text-muted-foreground">@{item.targetUser?.username}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <input type="checkbox" checked={item.showBadge} onChange={() => handleToggleBadge(item)} className="accent-primary" />
          {t('admin.pin')}
        </label>
        <button
          onClick={() => handleRemove(item.id)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  const renderChannelRow = (item: FeaturedItem) => (
    <div
      key={item.id}
      {...dragBind(item)}
      className={cn(
        'group flex items-center gap-2.5 px-4 py-2.5 transition-colors',
        overKey === item.id && dragKey !== item.id && 'bg-primary/5',
        dragKey === item.id && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        title="Drag to reorder"
        className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <Avatar seed={item.targetChannel?.slug || ''} type="channel" src={item.targetChannel?.logoUrl} className="h-8 w-8 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">//{item.targetChannel?.slug}</p>
        <p className="text-xs text-muted-foreground">
          {t('admin.members', { count: item.targetChannel?.memberCount ?? 0 })} · {t('admin.postsCount', { count: item.targetChannel?.postCount ?? 0 })}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <input type="checkbox" checked={item.showBadge} onChange={() => handleToggleBadge(item)} className="accent-primary" />
          {t('admin.pin')}
        </label>
        <button
          onClick={() => handleRemove(item.id)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  const renderAppRow = (item: FeaturedItem) => {
    const hidden = item.targetChannel?.listed === false;
    return (
      <div
        key={item.id}
        {...dragBind(item)}
        className={cn(
          'group flex items-center gap-2.5 px-4 py-2.5 transition-colors',
          overKey === item.id && dragKey !== item.id && 'bg-primary/5',
          dragKey === item.id && 'opacity-40',
        )}
      >
        <span
          aria-hidden
          title="Drag to reorder"
          className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <Avatar
          seed={item.targetChannel?.slug || ''}
          type="channel"
          src={item.targetChannel?.logoUrl}
          className={cn('h-8 w-8 shrink-0', hidden && 'opacity-50')}
        />
        <div className={cn('flex-1 min-w-0', hidden && 'opacity-50')}>
          <p className="text-sm font-medium truncate">{item.targetChannel?.name || item.targetChannel?.slug}</p>
          <p className="text-xs text-muted-foreground truncate">//{item.targetChannel?.slug}</p>
        </div>
        <button
          onClick={() => handleToggleListed(item)}
          className={cn(
            'text-xs px-2 py-0.5 rounded-full border transition-colors shrink-0',
            hidden
              ? 'border-muted-foreground/30 text-muted-foreground'
              : 'border-primary/30 text-primary bg-primary/5'
          )}
        >
          {hidden ? t('admin.hidden') : t('admin.listed')}
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <input type="checkbox" checked={item.showBadge} onChange={() => handleToggleBadge(item)} className="accent-primary" />
            {t('admin.pin')}
          </label>
          <button
            onClick={() => handleRemove(item.id)}
            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  };

  const renderTagRow = (item: FeaturedItem) => (
    <div
      key={item.id}
      {...dragBind(item)}
      className={cn(
        'group flex items-center gap-2.5 px-4 py-2.5 transition-colors',
        overKey === item.id && dragKey !== item.id && 'bg-primary/5',
        dragKey === item.id && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        title="Drag to reorder"
        className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Hash className="h-4 w-4 text-muted-foreground" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">#{item.targetTag?.name}</p>
        <p className="text-xs text-muted-foreground">{t('admin.uses', { count: item.targetTag?.useCount ?? 0 })}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <input type="checkbox" checked={item.showBadge} onChange={() => handleToggleBadge(item)} className="accent-primary" />
          {t('admin.pin')}
        </label>
        <button
          onClick={() => handleRemove(item.id)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  const renderUserRow = (item: FeaturedItem) => (
    <div
      key={item.id}
      {...dragBind(item)}
      className={cn(
        'group flex items-center gap-2.5 px-4 py-2.5 transition-colors',
        overKey === item.id && dragKey !== item.id && 'bg-primary/5',
        dragKey === item.id && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        title="Drag to reorder"
        className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <Avatar seed={item.targetUser?.username || ''} type="human" src={item.targetUser?.avatarUrl} className="h-8 w-8 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.targetUser?.displayName || item.targetUser?.username}</p>
        <p className="text-xs text-muted-foreground">@{item.targetUser?.username}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <input type="checkbox" checked={item.showBadge} onChange={() => handleToggleBadge(item)} className="accent-primary" />
          {t('admin.pin')}
        </label>
        <button
          onClick={() => handleRemove(item.id)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  const renderSpacerRow = (ct: string, entry: Extract<SectionEntry, { kind: 'spacer' }>) => (
    <div
      key={entry.key}
      {...entryDrag(ct, entry.key)}
      className={cn(
        'group flex items-center gap-2.5 px-4 py-2 transition-colors bg-muted/30',
        overKey === entry.key && dragKey !== entry.key && 'bg-primary/5',
        dragKey === entry.key && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        title="Drag to reorder"
        className="shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="h-8 w-8 rounded-lg border border-dashed border-muted-foreground/30 flex items-center justify-center shrink-0">
        <Wand2 className="h-4 w-4 text-muted-foreground/60" />
      </span>
      <p className="flex-1 min-w-0 text-sm text-muted-foreground italic truncate">{t('admin.spacerLabel')}</p>
      <div className="flex items-center gap-1.5 shrink-0">
        <input
          type="number"
          min={1}
          max={50}
          value={entry.size}
          onChange={(e) => {
            // Floor at 1: `handleSpacerResize(…, 0)` is the DELETE path (what
            // the trash button calls), so typing or backspacing to a zero here
            // used to make the spacer disappear mid-edit. `min` does not stop
            // a keystroke, only form validation.
            const n = parseInt(e.target.value, 10);
            if (!Number.isNaN(n) && n >= 1) handleSpacerResize(ct, entry.key, n);
          }}
          className="w-14 px-1.5 py-0.5 text-sm text-center rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <span className="text-xs text-muted-foreground">{t('admin.spacerSlots')}</span>
        <button
          onClick={() => handleSpacerResize(ct, entry.key, 0)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  // const carouselPostIds = new Set(carouselItems.map(i => i.postId).filter(Boolean) as string[]);
  const featuredPostIds = new Set(featuredItems.map(i => i.postId).filter(Boolean) as string[]);
  const marketplacePostIds = new Set(marketplaceItems.map(i => i.postId).filter(Boolean) as string[]);
  const agentIds = new Set(trendingAgents.map(i => i.targetUserId).filter(Boolean) as string[]);
  const channelIds = new Set(trendingChannels.map(i => i.targetChannelId).filter(Boolean) as string[]);
  const appIds = new Set(curatedApps.map(i => i.targetChannelId).filter(Boolean) as string[]);
  const tagIds = new Set(featuredTags.map(i => (i as any).targetTag?.id).filter(Boolean) as string[]);
  const userIds = new Set(featuredUsers.map(i => i.targetUserId).filter(Boolean) as string[]);

  return (
    <div className="space-y-8">
      {addError && (
        <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
          {addError}
        </div>
      )}

      {/* Carousel Posts - commented out
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Carousel Posts</h3>
            <span className="text-xs text-muted-foreground ml-auto">{carouselItems.length} posts</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Featured posts in the top carousel. Admin-picked first, then filled algorithmically to 20.
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {carouselItems.sort((a, b) => a.sortOrder - b.sortOrder).map((item, i) =>
            renderPostRow(item, i, carouselItems.length, 'carousel_post')
          )}
          {carouselItems.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">No carousel posts yet. Algorithmic fallback is active.</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'carousel_post' ? (
            <SearchPicker
              searchType="posts"
              existingIds={carouselPostIds}
              onSelect={(result) => handleAddPost('carousel_post', result)}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <button
              onClick={() => { setAddingType('carousel_post'); setAddError(''); }}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
            >
              <Plus className="h-3.5 w-3.5" />
              Add post
            </button>
          )}
        </div>
      </div>
      */}

      {/* Featured Posts */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.postsTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.postsCount', { count: featuredItems.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.postsDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('featured_post').map((e, i) =>
            e.kind === 'spacer'
              ? renderSpacerRow('featured_post', e)
              : renderPostRow(e.item, i, featuredItems.length, 'featured_post')
          )}
          {featuredItems.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noPostsYet')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'featured_post' ? (
            <SearchPicker
              searchType="posts"
              existingIds={featuredPostIds}
              onSelect={(result) => handleAddPost('featured_post', result)}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('featured_post'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addPost')}
              </button>
              {featuredItems.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('featured_post')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Apps ladder — the order the catalog is browsed in */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <AppWindow className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.appsTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.appsCount', { count: curatedApps.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.appsDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('featured_app').map((e) =>
            e.kind === 'spacer' ? renderSpacerRow('featured_app', e) : renderAppRow(e.item)
          )}
          {curatedApps.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noApps')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'featured_app' ? (
            <SearchPicker
              searchType="apps"
              existingIds={appIds}
              onSelect={handleAddApp}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('featured_app'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addApp')}
              </button>
              {curatedApps.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('featured_app')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trending Agents */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.agentsTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.agentsCount', { count: trendingAgents.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.agentsDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('trending_agent').map((e) =>
            e.kind === 'spacer' ? renderSpacerRow('trending_agent', e) : renderAgentRow(e.item)
          )}
          {trendingAgents.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noAgents')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'trending_agent' ? (
            <SearchPicker
              searchType="agents"
              existingIds={agentIds}
              onSelect={handleAddAgent}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('trending_agent'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addAgent')}
              </button>
              {trendingAgents.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('trending_agent')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trending Channels */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.channelsTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.channelsCount', { count: trendingChannels.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.channelsDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('trending_channel').map((e) =>
            e.kind === 'spacer' ? renderSpacerRow('trending_channel', e) : renderChannelRow(e.item)
          )}
          {trendingChannels.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noChannels')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'trending_channel' ? (
            <SearchPicker
              searchType="channels"
              existingIds={channelIds}
              onSelect={handleAddChannel}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('trending_channel'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addChannel')}
              </button>
              {trendingChannels.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('trending_channel')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Featured Tags */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.tagsTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.tagsCount', { count: featuredTags.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.tagsDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('featured_tag').map((e) =>
            e.kind === 'spacer' ? renderSpacerRow('featured_tag', e) : renderTagRow(e.item)
          )}
          {featuredTags.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noTags')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'featured_tag' ? (
            <SearchPicker
              searchType="tags"
              existingIds={tagIds}
              onSelect={handleAddTag}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('featured_tag'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addTag')}
              </button>
              {featuredTags.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('featured_tag')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Who to Follow */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.humansTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.humansCount', { count: featuredUsers.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.humansDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('featured_user').map((e) =>
            e.kind === 'spacer' ? renderSpacerRow('featured_user', e) : renderUserRow(e.item)
          )}
          {featuredUsers.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noUsers')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'featured_user' ? (
            <SearchPicker
              searchType="users"
              existingIds={userIds}
              onSelect={handleAddUser}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('featured_user'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addUser')}
              </button>
              {featuredUsers.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('featured_user')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Featured Marketplace */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{t('admin.marketplaceTitle')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{t('admin.marketplaceCount', { count: marketplaceItems.length })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('admin.marketplaceDesc')}
          </p>
        </div>
        <div className="divide-y divide-border/50">
          {entriesForType('marketplace_post').map((e, i) =>
            e.kind === 'spacer'
              ? renderSpacerRow('marketplace_post', e)
              : renderPostRow(e.item, i, marketplaceItems.length, 'marketplace_post')
          )}
          {marketplaceItems.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-3">{t('admin.noMarketplace')}</p>
          )}
        </div>
        <div className="border-t border-border/50">
          {addingType === 'marketplace_post' ? (
            <SearchPicker
              searchType="posts"
              existingIds={marketplacePostIds}
              onSelect={(result) => handleAddPost('marketplace_post', result)}
              onCancel={() => setAddingType(null)}
            />
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => { setAddingType('marketplace_post'); setAddError(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('admin.addMarketplacePost')}
              </button>
              {marketplaceItems.length > 0 && (
                <button
                  onClick={() => handleAddSpacer('marketplace_post')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {t('admin.addSpacer')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminPageInner({ embedded = false }: { embedded?: boolean } = {}) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  /**
   * `?tab=` is how the retired /admin/* URLs deep-link in (see
   * lib/workspaces/redirect-to-dialog.ts). Read once as the initial value: the
   * tab is local state afterwards, so clicking a tab does not fight the URL.
   */
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'content');
  const [navOpen, setNavOpen] = useState(false);
  const t = useT();

  useEffect(() => {
    if (!loading && (!user || (user.role !== 'admin' && user.role !== 'moderator'))) {
      router.replace('/');
    }
  }, [user, loading, router]);

  if (loading || !user || (user.role !== 'admin' && user.role !== 'moderator')) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="2xl" tone="muted" />
      </div>
    );
  }

  const isAdmin = user.role === 'admin';

  /**
   * One admin navigation. The four money/mail surfaces used to be unlinked
   * URL-only pages; they are tabs here and their old URLs redirect in with
   * `?tab=`.
   *
   * `adminOnly` hides a tab a moderator cannot use at all. It is presentation,
   * not protection: every route behind these tabs enforces its own role check
   * server-side, because a hidden tab is not a permission check.
   */
  const tabs: { id: string; label: string; icon: typeof LayoutGrid; adminOnly?: boolean }[] = [
    { id: 'content', label: t('admin.curatedContent'), icon: LayoutGrid },
    // Operational surface first among the data tabs: during launch weeks
    // Health is what the dialog gets opened for (V. request 2026-08-05).
    { id: 'health', label: 'Health', icon: HeartPulse },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'moderation', label: 'Moderation', icon: Flag },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'earnings', label: 'Payouts', icon: Banknote, adminOnly: true },
    { id: 'attribution', label: 'Attribution', icon: Coins },
    // W7 finance layer: the cashflow readout. Admin only because the route
    // serves the liability numbers (the money desk's bar, like Payouts).
    { id: 'economics', label: 'Economics', icon: Landmark, adminOnly: true },
    { id: 'deck', label: 'Deck', icon: Presentation },
    // Ads in the app chrome: the gate (nobody by default), the house ads and
    // their counts. Staff read, admin write, both enforced by the routes.
    { id: 'ads', label: 'Ads', icon: Megaphone },
    // Models, providers and routes as data (model-registry-and-admin-plan.md
    // section 10.1, 2026-10-06): which models are offered, each one's
    // fallback chain of providers with their costs, sync and approval. Staff
    // read, admin write (decision 17.10), both enforced by /api/admin/models.
    // The registry switch itself stays in Settings; this tab shows its state.
    { id: 'models', label: t('admin.modelsTab'), icon: Cpu },
    // Money and legal posture: readable by staff, writable by admins only (the
    // route enforces both). Last because it is the rarest surface to need.
    { id: 'settings', label: 'Settings', icon: Settings2 },
  ].filter((tab) => !tab.adminOnly || isAdmin);

  /**
   * A `?tab=` deep link can name a tab this viewer cannot see (a moderator
   * following a link to Payouts) or one that no longer exists. Fall back to the
   * first visible tab rather than rendering an empty pane.
   */
  const effectiveTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : tabs[0].id;

  return (
    <div className={embedded ? 'w-full' : 'max-w-5xl mx-auto'}>
      {!embedded && <h1 className="text-xl font-bold mb-6">{t('admin.title')}</h1>}

      <div className="flex flex-col @lg/main:flex-row gap-6">
        {/* Tab navigation */}
        {/* Sticky beside the content on a wide dialog: on a long tab (Ads is
            over 4,000 px) the tab list scrolled away with the page (the
            design pass of 2026-09-30). */}
        <nav className="@lg/main:w-52 shrink-0 @lg/main:sticky @lg/main:top-0 @lg/main:self-start">
          {(() => {
            const active = tabs.find(t => t.id === effectiveTab);
            const ActiveIcon = active?.icon || LayoutGrid;
            return (
              <button
                onClick={() => setNavOpen(!navOpen)}
                className="@lg/main:hidden w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border text-sm font-medium"
              >
                <span className="flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" />
                  {active?.label || t('admin.title')}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${navOpen ? 'rotate-180' : ''}`} />
              </button>
            );
          })()}

          <div className={`${navOpen ? 'block' : 'hidden'} @lg/main:block @lg/main:relative @lg/main:mt-0 @lg/main:shadow-none @lg/main:border-0 @lg/main:bg-transparent @lg/main:rounded-none @lg/main:max-h-none absolute left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg max-h-[60vh] overflow-y-auto space-y-0.5 p-1 @lg/main:p-0`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  effectiveTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        {/* `overflow-x-clip`, not `overflow-hidden`: hidden makes this box a
            scroll container, so a `position: sticky` bar inside a tab stuck
            to THIS box (which never scrolls) instead of to the dialog. The
            Ads tab's save bar sat 3,400 px down, out of view, with its
            sticky doing nothing (the design pass of 2026-09-30, finding 10).
            Clip keeps a wide table from widening the page and creates no
            scroll container. */}
        <div className="flex-1 min-w-0 overflow-x-clip">
          {effectiveTab === 'content' && <ContentTab />}
          {effectiveTab === 'health' && <HealthTab />}
          {effectiveTab === 'analytics' && <AnalyticsTab />}
          {effectiveTab === 'moderation' && <ModerationTab />}
          {effectiveTab === 'email' && <EmailTab />}
          {effectiveTab === 'earnings' && <EarningsTab />}
          {effectiveTab === 'attribution' && <AttributionTab />}
          {effectiveTab === 'economics' && <EconomicsTab />}
          {effectiveTab === 'deck' && <DeckTab />}
          {effectiveTab === 'ads' && <AdsTab />}
          {effectiveTab === 'models' && <ModelsTab />}
          {effectiveTab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}

/**
 * Core admin view. Used both as the standalone `/admin` page and
 * embedded inside the workspace's full-screen dialog system. When
 * `embedded` is true we drop the page's own `<h1>` and the
 * `max-w-5xl mx-auto` width cap so the dialog frame can supply its own
 * header and bound the width itself.
 */
export function AdminView({ embedded = false }: { embedded?: boolean } = {}) {
  return <AdminPageInner embedded={embedded} />;
}
